import React, { useState, useEffect } from "react";
import { Dialog, Grid } from "@material-ui/core";
import ReportTerminationInfo from "./ReportUserTerminationInfo";
import FinalPayDetails from "./FinalPayDetails";
import { DPButton } from "Components/DP";
import { useReportTerminationContext } from "./ReportTerminationContext";
import TerminateUserSubmitButton from "./SubmitButtons/TerminateUserSubmitButton";
import UpdateUserFinalPaySubmitButton from "./SubmitButtons/UpdateUserFinalPaySubmitButton";
import DPPermissionsDialog from "Components/DP/DPPermissionsDialog/DPPermissionsDialog";
import queries from "Shared/queries";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type Steps = "ReportUserTermination" | "FinalPayDetails";
type stepsGraph = {
  [key in Steps]: {
    view: JSX.Element;
    submitButton: JSX.Element;
  }
};

const ReportTerminationModal = ({ isOpen, onClose }: Props) => {
  const [step, setStep] = useState<Steps>("ReportUserTermination");
  const [isPermissionsDialogOpen, setIsPermissionsDialogOpen] = useState(false);
  const { employeeProfileId, dispatch } = useReportTerminationContext();

  const handleClose = () => {
    dispatch({ type: "resetValues" });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setStep("ReportUserTermination");
    }
  }, [isOpen]);

  const onTerminateUserHandler = (deductionAmountCents: number) => {
    if (!deductionAmountCents) {
      handleClose();
    } else {
      dispatch({
        type: "updateDeductionAmountCents",
        deductionAmountCents: deductionAmountCents,
      });
      setStep("FinalPayDetails");
    }
  };

  const updateUserFinalPayHandler = () => {
    handleClose();
  };

  const onErrorHandler = () => {
    setIsPermissionsDialogOpen(true);
  };

  const steps: stepsGraph = {
    ReportUserTermination: {
      view: <ReportTerminationInfo />,
      submitButton: (
        <TerminateUserSubmitButton
          onError={onErrorHandler}
          onSubmit={onTerminateUserHandler}
          employeeProfileId={employeeProfileId}
          refetchQueries={[
            {
              query: queries.GET_EMPLOYEE_PROFILE,
              variables: {
                employeeProfileId,
              },
            },
          ]}
        />
      ),
    },
    FinalPayDetails: {
      view: <FinalPayDetails />,
      submitButton: (
        <UpdateUserFinalPaySubmitButton
          onSubmit={updateUserFinalPayHandler}
        />
      ),
    },
  };

  const handlePermissionsDialogClose = () => {
    setIsPermissionsDialogOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} id={"terminate-employee-modal"}>
      {steps[step].view}
      <Grid container justify="flex-end">
        {step !== "FinalPayDetails" && (
          <DPButton
            id="terminate-employee-cancel-button"
            align="right"
            color="default"
            onClick={handleClose}
          >
            Cancel
          </DPButton>
        )}
        {steps[step].submitButton}
      </Grid>
      <DPPermissionsDialog
        isOpen={isPermissionsDialogOpen}
        onClose={handlePermissionsDialogClose}
      />
    </Dialog>
  );
};

export default ReportTerminationModal;
